import { SessionPin } from "./SessionPin";
import { Archive, EllipsisVertical, Plus } from "lucide-react";
import type { DragEvent, ReactElement, ReactNode } from "react";

import { cn } from "~/lib/utils";
import { ROOT_SESSION_STATUS_LABELS, type RootSessionStatus } from "~/rootSessionStrip";
import { SessionRing } from "./SessionRing";
import {
  RAIL_ARCHIVE_CONFIRM_CLASS,
  RAIL_ROW_CONTAINER_CLASS,
  RAIL_WIDE_ROW_ONLY_CLASS,
  RAIL_WIDE_ROW_SEAT_CLASS,
  railAddedSeatsClass,
  railFloatingMenuClass,
  railFloatingSeatClass,
} from "./railArchiveConfirm";
import { RailHoverCard } from "./RailHoverCard";
import type { RailHoverFacts } from "./railHoverCard.logic";

/**
 * A SUB-SESSION row under its parent session in the Room Rail (RM-CAN-10 law
 * 3: every nested row is its own door with its own verbs). The ring wears
 * the session's own status; hover and focus reveal pin, ⋮, and retire.
 * A pinned session keeps its pin visible. Right-click is the fast door.
 * Depth indents from the parent's edge. Pure: the caller wires
 * every verb.
 */
export function SessionRow({
  sessionId,
  label,
  status,
  depth,
  current,
  pinned = false,
  onAddress,
  onTogglePin,
  onMenu,
  onRetire,
  onArchive,
  archiveConfirming = false,
  onArchiveLeave,
  onNewChild,
  renaming = null,
  floatSeats = false,
  draftMark,
  fileDrop,
  fileDropTarget = false,
  preview = null,
  previewDisabled = false,
  hand = null,
}: {
  /** Cleo launched this session for a hand: what she asked, under which session. */
  readonly hand?: { readonly ask: string; readonly parentLabel: string } | null;
  readonly sessionId: string;
  readonly label: string;
  readonly status: RootSessionStatus | null;
  /** 1 for a child of a chip, 2 for a grandchild, … */
  readonly depth: number;
  readonly current: boolean;
  readonly pinned?: boolean;
  readonly onAddress: () => void;
  /** Absent where the stance keeps no pins (RM-CAN-14: Local); the seat is not drawn. */
  readonly onTogglePin?: (() => void) | undefined;
  readonly onMenu: (position: { x: number; y: number }) => void;
  /** Absent where the stance has no retirement (RM-CAN-14: Local); the seat is not drawn. */
  readonly onRetire?: (() => void) | undefined;
  /** Inline rename, as the root chip has it: the menu's Rename reaches nested rows too. */
  readonly renaming?: {
    readonly title: string;
    readonly onChange: (title: string) => void;
    readonly onCommit: () => void;
    readonly onCancel: () => void;
  } | null;
  /** RM-CAN-14: the unpinned pin, the menu and the retire glyph join the floating seats (RailRowOrgans.hoverSeats). */
  readonly floatSeats?: boolean;
  /** RM-CAN-14: the ungoverned leaving verb (Local archives a conversation). */
  readonly onArchive?: (() => void) | undefined;
  /** The archive seat awaits its second click: it reads "Confirm", in view without a hover. */
  readonly archiveConfirming?: boolean;
  /** The pointer left the row while it was asking: the question is withdrawn. */
  readonly onArchiveLeave?: (() => void) | undefined;
  /** RM-CAN-14: the new-child verb, where the stance seats it on the rail. */
  readonly onNewChild?: (() => void) | undefined;
  /** UP13: the unsent-draft mark (pen + hover discard), rendered by the caller. */
  readonly draftMark?: ReactNode;
  /** UP13: file drops attach to this session and open it; nothing is sent. */
  readonly fileDrop?:
    | {
        readonly onDragOver: (event: DragEvent<HTMLElement>) => void;
        readonly onDragLeave: (event: DragEvent<HTMLElement>) => void;
        readonly onDrop: (event: DragEvent<HTMLElement>) => void;
      }
    | undefined;
  readonly fileDropTarget?: boolean;
  /** RM-CAN-10 hover card (2026-09-15): the full name and times beside the rail; replaces the native title. */
  readonly preview?: RailHoverFacts | null;
  readonly previewDisabled?: boolean;
}): ReactElement {
  const named =
    hand === null ? label : `${label} · Cleo's hand for ${hand.parentLabel}: ${hand.ask}`;
  const title = status === null ? named : `${named} · ${ROOT_SESSION_STATUS_LABELS[status]}`;
  // The retire glyph, in line (reserved) or in the cluster (floating): the same door.
  const retireSeat = (className: string): ReactElement => (
    <span
      aria-hidden="true"
      className={className}
      data-room-rail-retire={sessionId}
      onClick={(event) => {
        event.stopPropagation();
        onRetire?.();
      }}
      title="Retire session"
    >
      <Archive className="size-3" />
    </span>
  );
  const row = (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        // The menu by keyboard, as the root chip has it (FD-CAN-20: every row is a door).
        if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          onMenu({ x: rect.left, y: rect.bottom + 4 });
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onAddress();
        }
      }}
      aria-current={current ? "true" : undefined}
      aria-label={pinned ? `Pinned · ${title}` : title}
      className={cn(
        "group/row relative flex h-6 w-full min-w-0 items-center gap-1.5 rounded-lg py-0 pr-1.5 text-left text-[11px] outline-hidden transition-colors hover:bg-accent/40 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring",
        current ? "bg-accent/30 text-foreground" : "text-muted-foreground",
        floatSeats && RAIL_ROW_CONTAINER_CLASS,
        fileDropTarget && "ring-1 ring-ring bg-accent/40",
      )}
      data-room-rail-depth={depth}
      data-room-rail-hand={hand === null ? undefined : ""}
      data-room-rail-file-drop={fileDropTarget ? "true" : undefined}
      data-room-rail-session={sessionId}
      {...fileDrop}
      onMouseLeave={archiveConfirming ? onArchiveLeave : undefined}
      onClick={onAddress}
      onContextMenu={(event) => {
        event.preventDefault();
        onMenu({ x: event.clientX, y: event.clientY });
      }}
      style={{ paddingLeft: `${6 + depth * 18}px` }}
      title={preview ? undefined : title}
    >
      <SessionRing className="size-3" status={status} />
      {renaming ? (
        <input
          autoFocus
          className="min-w-0 flex-1 bg-transparent text-[11px] text-foreground outline-none"
          data-room-rail-rename=""
          onBlur={() => renaming.onCommit()}
          onChange={(event) => renaming.onChange(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") renaming.onCommit();
            else if (event.key === "Escape") renaming.onCancel();
          }}
          value={renaming.title}
        />
      ) : (
        <>
          {hand === null ? null : (
            <span
              className="shrink-0 rounded-[4px] border border-current/40 px-1 text-[9px] leading-[14px] tracking-wide opacity-80"
              data-room-rail-hand-mark=""
            >
              Hand
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">{label}</span>
        </>
      )}
      {draftMark}
      {onTogglePin && (!floatSeats || pinned) ? (
        <SessionPin label={label} pinned={pinned} onToggle={onTogglePin} />
      ) : null}
      {floatSeats ? null : (
        <span
          aria-hidden="true"
          className="pointer-events-none inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm opacity-0 hover:bg-accent hover:opacity-100 group-hover/row:pointer-events-auto group-hover/row:opacity-60 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-60"
          data-room-rail-menu={sessionId}
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            onMenu({ x: rect.left, y: rect.bottom + 4 });
          }}
        >
          <EllipsisVertical className="size-3" />
        </span>
      )}
      {onRetire && !floatSeats
        ? retireSeat(
            "pointer-events-none inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm opacity-0 hover:bg-accent hover:opacity-100 group-hover/row:pointer-events-auto group-hover/row:opacity-60 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-60",
          )
        : null}
      {floatSeats || onNewChild || onArchive ? (
        // The seats a stance adds float over the label's tail (railArchiveConfirm.ts).
        <span
          className={railAddedSeatsClass("row", archiveConfirming)}
          data-room-rail-added-seats={sessionId}
          onClick={(event) => event.stopPropagation()}
          style={{ right: `${floatSeats ? 6 : onRetire ? 66 : 46}px` }}
        >
          {floatSeats && onTogglePin && !pinned ? (
            <span className={RAIL_WIDE_ROW_ONLY_CLASS}>
              <SessionPin label={label} pinned={pinned} onToggle={onTogglePin} />
            </span>
          ) : null}
          {onNewChild ? (
            <span
              aria-label={`New child of ${label}`}
              className={RAIL_WIDE_ROW_SEAT_CLASS}
              data-room-rail-new-child={sessionId}
              onClick={(event) => {
                event.stopPropagation();
                onNewChild();
              }}
              role="button"
              title="New child"
            >
              <Plus aria-hidden="true" className="size-3" />
            </span>
          ) : null}
          {floatSeats ? (
            <span
              aria-hidden="true"
              className={railFloatingMenuClass("row")}
              data-room-rail-menu={sessionId}
              onClick={(event) => {
                event.stopPropagation();
                const rect = event.currentTarget.getBoundingClientRect();
                onMenu({ x: rect.left, y: rect.bottom + 4 });
              }}
            >
              <EllipsisVertical className="size-3" />
            </span>
          ) : null}
          {floatSeats && onRetire ? retireSeat(railFloatingSeatClass("row")) : null}
          {onArchive && archiveConfirming ? (
            <span
              aria-label={`Confirm archive ${label}`}
              className={RAIL_ARCHIVE_CONFIRM_CLASS}
              data-room-rail-archive-confirm={sessionId}
              onClick={(event) => {
                event.stopPropagation();
                onArchive();
              }}
              role="button"
            >
              Confirm
            </span>
          ) : onArchive ? (
            <span
              aria-label={`Archive ${label}`}
              className={RAIL_WIDE_ROW_SEAT_CLASS}
              data-room-rail-archive-session={sessionId}
              onClick={(event) => {
                event.stopPropagation();
                onArchive();
              }}
              role="button"
              title="Archive"
            >
              <Archive className="size-3" />
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
  return preview ? <RailHoverCard disabled={previewDisabled} facts={preview} trigger={row} /> : row;
}
